import {
  DASHBOARD_PATH,
  PROJECT_FORM_PATHS,
  PROJECTS_HUB_PATHS,
  PUBLIC_STATUS_PATH,
  SERVER_FORM_PATHS,
  SERVERS_HUB_PATHS,
  SETTINGS_PATHS,
  SITE_FORM_PATHS,
} from './nav-paths.ts';

const NPC_LIST = 'npc server list';
const NPC_DEPLOY = 'npc deploy <project> <env> --ref <ref> --wait';
export const NPC_ENROLL = 'npc server add <id> --use-default-key --name <alias>';

const UNHEALTHY_RANK: Record<string, number> = {
  offline: 0,
  unknown: 1,
  pending_probe: 2,
  starting: 3,
};

export function spatialTitleKey(pathname: string): string {
  if (pathname === DASHBOARD_PATH) return 'sidebar.dashboard';
  if (pathname === SITE_FORM_PATHS.create) return 'spatialForm.newSite';
  if (/^\/servers\/sites\/[^/]+\/edit$/.test(pathname)) return 'spatialForm.editSite';
  if (pathname === SERVER_FORM_PATHS.create) return 'spatialForm.newServer';
  if (/^\/servers\/[^/]+\/edit$/.test(pathname)) return 'spatialForm.editServer';
  if (pathname === PROJECT_FORM_PATHS.create) return 'spatialForm.newProject';
  if (/^\/projects\/[^/]+\/edit$/.test(pathname)) return 'spatialForm.editProject';
  if (/^\/projects\/[^/]+\/environments\/new$/.test(pathname)) return 'spatialForm.newEnvironment';
  if (/^\/projects\/[^/]+\/release-plans\/new$/.test(pathname)) return 'spatialForm.newReleasePlan';
  if (pathname.startsWith(SERVERS_HUB_PATHS.runtime)) return 'sidebar.runtime';
  if (pathname.startsWith(SERVERS_HUB_PATHS.services)) return 'sidebar.services';
  if (pathname.startsWith(SERVERS_HUB_PATHS.sites)) return 'sidebar.sites';
  if (pathname.startsWith(SERVERS_HUB_PATHS.domainCertificates)) return 'sidebar.domainCertificates';
  if (pathname.startsWith(SERVERS_HUB_PATHS.timeline)) return 'sidebar.timeline';
  if (pathname.startsWith(SERVERS_HUB_PATHS.list)) return 'sidebar.servers';
  if (pathname.startsWith(PROJECTS_HUB_PATHS.repos)) return 'projectsHub.reposTab';
  if (pathname.startsWith(PROJECTS_HUB_PATHS.releases)) return 'projectsHub.releasesTab';
  if (pathname.startsWith(PROJECTS_HUB_PATHS.runs)) return 'projectsHub.runsTab';
  if (pathname.startsWith(PROJECTS_HUB_PATHS.list)) return 'sidebar.projects';
  if (pathname.startsWith(SETTINGS_PATHS.sshKeys)) return 'sidebar.sshKeys';
  if (pathname.startsWith(SETTINGS_PATHS.teams)) return 'sidebar.teams';
  if (pathname.startsWith(SETTINGS_PATHS.cloudflare)) return 'sidebar.cloudflare';
  if (pathname.startsWith(SETTINGS_PATHS.general)) return 'sidebar.settings';
  if (pathname.startsWith(PUBLIC_STATUS_PATH)) return 'sidebar.publicStatus';
  if (pathname.startsWith('/logs')) return 'sidebar.logs';
  if (pathname.startsWith('/secrets')) return 'sidebar.secrets';
  if (pathname.startsWith('/ai-driven')) return 'sidebar.aiDriven';
  return 'sidebar.dashboard';
}

const COMMENT = {
  runtime: '# 运行环境跨项目；行内复制 npc env show <project> <env> --section review',
  services: '# 服务按主机列出；npc server service list <server>',
  timeline: '# 时间线按主机；npc server handoff index <server>',
  logs: '# 日志查询在面板里做；采集器安装走 npc server probe install',
  ai: '# 密钥签发在面板里做一次；之后 agent 只走 npc',
  mcp: '# MCP 是本机没有 npc 时的退路；调用记录没有对应的 npc 命令',
  publicStatus: '# 公开状态页配置没有对应的 npc 命令',
  notifications: '# 通知没有对应的 npc 命令',
} as const;

function pathId(pathname: string, index: number): string {
  return decodeURIComponent(pathname.split('/')[index] ?? '');
}

export function spatialNpcCommand(pathname: string): string {
  if (pathname === SERVER_FORM_PATHS.create) return NPC_ENROLL;
  if (pathname.startsWith(SETTINGS_PATHS.cloudflare)) return 'npc cf token check';
  if (pathname.startsWith('/settings')) return 'npc auth info';
  if (pathname.startsWith(SERVERS_HUB_PATHS.runtime)) return COMMENT.runtime;
  if (pathname.startsWith(SERVERS_HUB_PATHS.services)) return COMMENT.services;
  if (pathname.startsWith(SERVERS_HUB_PATHS.sites) || pathname.startsWith(SERVERS_HUB_PATHS.domainCertificates)) {
    return 'npc cert list';
  }
  if (pathname.startsWith(SERVERS_HUB_PATHS.timeline)) return COMMENT.timeline;
  if (pathname.startsWith(`${SERVERS_HUB_PATHS.list}/`)) {
    const id = pathId(pathname, 2);
    if (id && id !== 'new') return `npc server handoff brief ${quoteNpcArg(id)} --section agentSummary`;
  }
  if (pathname.startsWith('/secrets')) return 'npc vault list';
  if (pathname.startsWith('/logs')) return COMMENT.logs;
  if (pathname.startsWith('/ai-driven')) return COMMENT.ai;
  if (pathname.startsWith('/mcp-activity')) return COMMENT.mcp;
  if (pathname.startsWith(PUBLIC_STATUS_PATH)) return COMMENT.publicStatus;
  if (pathname.startsWith('/notifications')) return COMMENT.notifications;
  if (pathname.startsWith('/workflows') || pathname.startsWith(PROJECTS_HUB_PATHS.repos)) return 'npc project list';
  if (
    pathname.startsWith(PROJECTS_HUB_PATHS.releases) ||
    pathname.startsWith(PROJECTS_HUB_PATHS.runs) ||
    pathname.startsWith('/deployments') ||
    pathname.startsWith('/pipeline-runs')
  ) {
    return NPC_DEPLOY;
  }
  const envNew = pathname.match(/^\/projects\/([^/]+)\/environments\/new$/);
  if (envNew) {
    return `npc env create ${quoteNpcArg(decodeURIComponent(envNew[1]))} <env> --strategy compose_stack --server <server-ref>`;
  }
  const envShow = pathname.match(/^\/projects\/([^/]+)\/environments\/([^/]+)/);
  if (envShow) {
    return `npc env show ${quoteNpcArg(decodeURIComponent(envShow[1]))} ${quoteNpcArg(decodeURIComponent(envShow[2]))} --section review`;
  }
  if (pathname === PROJECT_FORM_PATHS.create || pathname === PROJECTS_HUB_PATHS.list) return 'npc project list';
  const project = pathname.match(/^\/projects\/([^/]+)/);
  if (project && !['new', 'repos', 'releases', 'runs'].includes(project[1])) {
    return `npc project handoff get ${quoteNpcArg(decodeURIComponent(project[1]))}`;
  }
  return NPC_LIST;
}

export function quoteNpcArg(value: string): string {
  if (/^[A-Za-z0-9._:-]+$/.test(value)) return value;
  return `'${value.replaceAll("'", `'\\''`)}'`;
}

export function pickUrgentHost<T extends { status: string; name: string }>(servers: T[]): T | null {
  const unhealthy = servers.filter((server) => server.status !== 'online');
  if (unhealthy.length === 0) return null;
  return [...unhealthy].sort((a, b) => {
    const rank = (UNHEALTHY_RANK[a.status] ?? 8) - (UNHEALTHY_RANK[b.status] ?? 8);
    if (rank !== 0) return rank;
    return a.name.localeCompare(b.name);
  })[0];
}

export function fleetNpcCommand(serverName?: string | null): string {
  const name = serverName?.trim();
  if (!name) return NPC_LIST;
  return `npc server handoff brief ${quoteNpcArg(name)} --section agentSummary`;
}
